/**
 * OrphanClientBanner — structural safety-net for projects with no client scope.
 *
 * Spec: keystone-client-required-per-category (R5).
 * Shown on ProjectDetailPage when:
 *   - project is NOT template
 *   - project is NOT archived
 *   - project is NOT in a proposal stage
 *   - no client scope exists (no scope with is_client_type=true)
 *
 * The action link lands on the same project page with ?action=add-client-scope
 * which is auto-wired by ProjectDetailPage (R8 plumbing) to open AddScopeModal
 * preselecting the client scope type.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { isProposalStage } from '@/utils/status';

interface OrphanClientBannerProps {
  project: {
    id: string;
    is_template?: boolean;
    is_archived?: boolean;
    workflow_stage?: string | null;
  };
  // undefined while the project's scopes are still loading.
  scopes: Array<{ is_client_type?: boolean | null }> | undefined;
}

export function shouldShowOrphanBanner(
  project: OrphanClientBannerProps['project'] | undefined | null,
  scopes: OrphanClientBannerProps['scopes'] | undefined | null,
): boolean {
  if (!project) return false;
  if (project.is_template) return false;
  if (project.is_archived) return false;
  if (isProposalStage(project.workflow_stage)) return false;
  // Scopes not loaded yet (or failed to load): say nothing. Treating "not
  // loaded" as "no client" flashed this banner on every reload of a project
  // that has a client (found by the v0.17 round-2 preview walk).
  if (scopes == null) return false;
  const hasClient = scopes.some((s) => s.is_client_type === true);
  return !hasClient;
}

export function OrphanClientBanner({ project, scopes }: OrphanClientBannerProps) {
  if (!shouldShowOrphanBanner(project, scopes)) return null;

  return (
    <div
      role="alert"
      data-testid="orphan-client-banner"
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-center justify-between gap-4"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">This project has no client assigned.</p>
          <p className="text-amber-800">Invoices and reports need a client.</p>
        </div>
      </div>
      <Link
        to={`/projects/${project.id}?action=add-client-scope`}
        className="inline-flex items-center rounded-md border border-amber-600 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
        data-testid="orphan-client-banner-add"
      >
        Add client now
      </Link>
    </div>
  );
}
